import { Text, Tr, Td, Checkbox, Input, Select } from '@chakra-ui/react'
import { useState, useMemo, useEffect } from 'react'
import { AllergyData } from '../../../../../../types/FirestoreUser'

interface AllergyHistoryRowProps {
  allergy: string
  age: number | undefined
  uid: string
  allergyHistory: AllergyData[]
  setAllergyHistory: (data: AllergyData[]) => void
}

export default function AllergyHistoryRow({
  allergy,
  age,
  uid,
  allergyHistory,
  setAllergyHistory,
}: AllergyHistoryRowProps) {
  const [isChecked, setIsChecked] = useState(false)
  const [severity, setSeverity] = useState('')
  const [ageDiagnosed, setAgeDiagnosed] = useState<number | undefined>()
  const [ageEnded, setAgeEnded] = useState<number | undefined>()
  const [estimatedYears, setEstimatedYears] = useState<number | undefined>()

  useEffect(() => {
    const existingAllergy = allergyHistory.find(
      (item) => item.allergy === allergy
    )
    if (existingAllergy) {
      setIsChecked(true)
      setSeverity(existingAllergy.severity || '')
      setAgeDiagnosed(existingAllergy.ageDiagnosed || undefined)
      setAgeEnded(existingAllergy.ageEnded || undefined)
      setEstimatedYears(existingAllergy.estimatedYears || undefined)
    } else {
      setIsChecked(false)
      setSeverity('')
      setAgeDiagnosed(undefined)
      setAgeEnded(undefined)
      setEstimatedYears(undefined)
    }
  }, [allergy, allergyHistory])

  const calculatedYears = useMemo(() => {
    if (ageDiagnosed) {
      if (ageEnded === undefined && age !== undefined) {
        return age - ageDiagnosed
      } else if (ageEnded) {
        return ageEnded - ageDiagnosed
      }
    }
    return estimatedYears
  }, [ageDiagnosed, ageEnded, age, estimatedYears])

  useEffect(() => {
    const updatedHistory = allergyHistory.filter(
      (item) => item.allergy !== allergy
    )

    if (isChecked) {
      updatedHistory.push({
        id: allergy,
        allergy,
        ageDiagnosed: ageDiagnosed ?? null,
        ageEnded: ageEnded ?? null,
        severity: severity as 'mild' | 'moderate' | 'severe' | null,
        estimatedYears: calculatedYears ?? null,
      })
    }

    setAllergyHistory(updatedHistory)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isChecked,
    ageDiagnosed,
    ageEnded,
    severity,
    calculatedYears,
    setAllergyHistory,
    allergy,
  ])

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const checked = e.target.checked
    setIsChecked(checked)
    if (!checked) {
      setSeverity('')
      setAgeDiagnosed(undefined)
      setAgeEnded(undefined)
      setEstimatedYears(undefined)
    }
  }

  function handleSeverityChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value
    setSeverity(value)
  }

  return (
    <Tr>
      <Td p={2}>
        <Text fontSize='sm'>{allergy}</Text>
      </Td>
      <Td textAlign='left' p={2}>
        <Checkbox
          name='allergy'
          isChecked={isChecked}
          onChange={handleCheckboxChange}
          ml={4}
        />
      </Td>
      <Td p={2}>
        <Input
          size='sm'
          type='number'
          name='ageDiagnosed'
          placeholder='Age Diagnosed'
          isDisabled={!isChecked}
          isRequired={isChecked}
          value={ageDiagnosed ?? ''}
          onChange={(e) => {
            const value = e.target.value
            setAgeDiagnosed(value === '' ? undefined : Number(value))
          }}
        />
      </Td>
      <Td p={2}>
        <Input
          size='sm'
          type='number'
          name='ageEnded'
          placeholder='Age Ended'
          isDisabled={!isChecked}
          value={ageEnded ?? ''}
          onChange={(e) => {
            const value = e.target.value
            setAgeEnded(value === '' ? undefined : Number(value))
          }}
        />
      </Td>
      <Td p={2}>
        <Select
          size='sm'
          placeholder='Severity'
          isDisabled={!isChecked}
          isRequired={isChecked}
          value={severity}
          name='severity'
          onChange={handleSeverityChange}
        >
          <option value='mild'>Mild</option>
          <option value='moderate'>Moderate</option>
          <option value='severe'>Severe</option>
        </Select>
      </Td>
      <Td p={2}>
        <Input
          size='sm'
          type='number'
          name='estimatedYears'
          placeholder='Estimated Years'
          isDisabled={!isChecked}
          value={calculatedYears ?? ''}
          onChange={(e) => {
            const value = e.target.value
            setEstimatedYears(value === '' ? undefined : Number(value))
          }}
        />
      </Td>
    </Tr>
  )
}
